// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/DistributionProfile.yaml
// Generated: 2026-06-25T16:43:33.493Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { distributionProfileService } from '@/services/finance/DistributionProfileService';
import { CreateDistributionProfile, UpdateDistributionProfile, DistributionProfile } from '@/types/finance/DistributionProfile';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for DistributionProfile
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const distributionprofileKeys = {
  all: (tenantId?: string | null) => ['distributionprofiles', tenantId] as const,
  lists: (tenantId?: string | null) => [...distributionprofileKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...distributionprofileKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...distributionprofileKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...distributionprofileKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all DistributionProfiles
 */
export function useDistributionProfiles(options?: Omit<UseQueryOptions<DistributionProfile[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<DistributionProfile[], Error>({
    queryKey: distributionprofileKeys.lists(tenantId),
    queryFn: () => distributionProfileService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single DistributionProfile by ID
 */
export function useDistributionProfile(
  id: string,
  options?: Omit<UseQueryOptions<DistributionProfile | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<DistributionProfile | undefined, Error>({
    queryKey: distributionprofileKeys.detail(id, tenantId),
    queryFn: () => distributionProfileService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new DistributionProfile
 */
export function useCreateDistributionProfile() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<DistributionProfile, Error, CreateDistributionProfile, { previousDistributionProfiles?: DistributionProfile[] }>({
    mutationFn: (data: CreateDistributionProfile) => distributionProfileService.create(data),
    onMutate: async (newDistributionProfile) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: distributionprofileKeys.lists(tenantId) });

      // Snapshot previous value
      const previousDistributionProfiles = queryClient.getQueryData<DistributionProfile[]>(distributionprofileKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<DistributionProfile[]>(distributionprofileKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newDistributionProfile, id: (newDistributionProfile as { id?: string }).id ?? 'temp-' + Date.now() } as DistributionProfile
      ]);

      return { previousDistributionProfiles };
    },
    onError: (_err, _newDistributionProfile, context) => {
      // Rollback on error
      if (context?.previousDistributionProfiles) {
        queryClient.setQueryData(distributionprofileKeys.lists(tenantId), context.previousDistributionProfiles);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: distributionprofileKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing DistributionProfile
 */
export function useUpdateDistributionProfile() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<DistributionProfile, Error, { id: string; data: UpdateDistributionProfile }, { previousDistributionProfile?: DistributionProfile; previousDistributionProfiles?: DistributionProfile[] }>({
    mutationFn: ({ id, data }) => distributionProfileService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: distributionprofileKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: distributionprofileKeys.lists(tenantId) });

      // Snapshot previous values
      const previousDistributionProfile = queryClient.getQueryData<DistributionProfile>(distributionprofileKeys.detail(id, tenantId));
      const previousDistributionProfiles = queryClient.getQueryData<DistributionProfile[]>(distributionprofileKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<DistributionProfile>(distributionprofileKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as DistributionProfile);

      // Optimistically update list
      queryClient.setQueryData<DistributionProfile[]>(distributionprofileKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as DistributionProfile : item))
      );

      return { previousDistributionProfile, previousDistributionProfiles };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousDistributionProfile) {
        queryClient.setQueryData(distributionprofileKeys.detail(id, tenantId), context.previousDistributionProfile);
      }
      if (context?.previousDistributionProfiles) {
        queryClient.setQueryData(distributionprofileKeys.lists(tenantId), context.previousDistributionProfiles);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: distributionprofileKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: distributionprofileKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a DistributionProfile
 */
export function useDeleteDistributionProfile() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousDistributionProfiles?: DistributionProfile[] }>({
    mutationFn: (id: string) => distributionProfileService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: distributionprofileKeys.lists(tenantId) });

      // Snapshot previous value
      const previousDistributionProfiles = queryClient.getQueryData<DistributionProfile[]>(distributionprofileKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<DistributionProfile[]>(distributionprofileKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousDistributionProfiles };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousDistributionProfiles) {
        queryClient.setQueryData(distributionprofileKeys.lists(tenantId), context.previousDistributionProfiles);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: distributionprofileKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSaveDistributionProfile() {
  const createMutation = useCreateDistributionProfile();
  const updateMutation = useUpdateDistributionProfile();

  return {
    mutate: (params: { id?: string; data: CreateDistributionProfile | UpdateDistributionProfile }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdateDistributionProfile }, options);
      } else {
        return createMutation.mutate(params.data as CreateDistributionProfile, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
